import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, User, ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { getBlogPostBySlug, getRelatedPosts } from '../lib/blog';
import { RelatedPosts } from '../components/RelatedPosts';
import { GlassCTA } from '../components/GlassCTA';
import { RotatingCallout } from '../components/RotatingCallout';
import { BackToTop } from '../components/BackToTop';
import { detectFirmIdsFromTitle, getRandomFirmId } from '../utils/detectFirmIdsFromTitle';
import type { BlogPostMeta } from '../lib/blog';
import type { BlogPost } from '../lib/supabase';

interface BlogPostData {
  meta: BlogPostMeta;
  Content: any;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostMeta[]>([]);
  
  // Function to convert BlogPostMeta to BlogPost for compatibility with RelatedPosts component
  const convertToBlogPost = (meta: BlogPostMeta): BlogPost => ({
    id: meta.slug, // Use slug as id since BlogPostMeta doesn't have an id
    title: meta.title,
    slug: meta.slug,
    excerpt: meta.excerpt,
    content: '', // Required by BlogPost but not used in RelatedPosts
    author: meta.author,
    date: meta.date,
    read_time: meta.readTime, // Map readTime to read_time
    image: meta.image,
    category: meta.category
  });

  useEffect(() => {
    if (slug) {
      getBlogPostBySlug(slug).then(data => {
        if (data) {
          setPost(data);
          getRelatedPosts(data.meta.slug, data.meta.category).then(posts => {
            setRelatedPosts(posts);
          });
        }
      });
    }
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-950 text-yellow-100 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Loading blog post...</h1>
        <p className="text-yellow-300">Please wait while we fetch the content.</p>
      </div>
    );
  }

  const { meta, Content } = post;
  const canonicalUrl = `https://www.propviper.com/blog/${meta.slug}`;
  
  // Ensure proper image URL construction with absolute paths
  // Social platforms require absolute URLs with no relative paths
  // Facebook has known issues with WebP images, so we need to use JPG/PNG
  let imageUrl = '';
  
  // First ensure we have an absolute URL
  if (meta.image) {
    // Check if it's already an absolute URL
    if (meta.image.startsWith('http')) {
      imageUrl = meta.image;
    } else {
      // Make it absolute with the correct domain
      imageUrl = `https://propviper.com${meta.image.startsWith('/') ? meta.image : `/${meta.image}`}`;
    }
  } else {
    // Use default image if none provided
    imageUrl = 'https://propviper.com/default-og-image.jpg';
  }
  
  // For Facebook, use the same image URL.
  // While Facebook prefers JPG/PNG, providing a valid WebP (if specified)
  // is better than a broken link to a non-existent JPG.
  // A long-term solution is to ensure JPG/PNG alternatives for all WebP thumbnails
  // or use a specific frontmatter field (e.g., facebookImage) if a different image is desired.
  let facebookImageUrl = imageUrl;
  
  // Ensure all image URLs use HTTPS for security and compatibility
  const secureImageUrl = imageUrl.replace(/^http:/i, 'https:');
  const secureFacebookImageUrl = facebookImageUrl.replace(/^http:/i, 'https:');
  
  // Facebook and LinkedIn recommended image dimensions
  const imageWidth = 1200;
  const imageHeight = 630;

  // 🔥 Dynamic firm detection
  const detectedFirms = detectFirmIdsFromTitle(meta.title || '');
  const topFirmId = detectedFirms[0] || getRandomFirmId();
  const bottomFirmId = detectedFirms[1] || getRandomFirmId([topFirmId]);

  return (
    <>
      <Helmet>
        <title>{`${meta.title} | PropViper Trading Blog`}</title>
        <meta name="description" content={meta.excerpt} />
        <meta name="author" content={meta.author} />
        <meta name="keywords" content={`prop trading, ${meta.category.toLowerCase()}, trading tips, prop firm guide`} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Primary meta tags - These are used by search engines and some social platforms */}
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.excerpt} />
        <meta name="author" content={meta.author} />
        
        {/* Standard image meta for search engines */}
        <meta name="image" content={secureImageUrl} />

        {/* Open Graph / Facebook / LinkedIn */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.excerpt} />
        <meta property="og:site_name" content="PropViper" />
        <meta property="og:locale" content="en_US" />
        
        {/* Facebook image - Always use JPG/PNG instead of WebP for Facebook */}
        <meta property="og:image" content={secureFacebookImageUrl} />
        <meta property="og:image:secure_url" content={secureFacebookImageUrl} />
        <meta property="og:image:width" content={imageWidth.toString()} />
        <meta property="og:image:height" content={imageHeight.toString()} />
        <meta property="og:image:alt" content={meta.title} />
        <meta property="og:image:type" content={secureFacebookImageUrl.toLowerCase().endsWith('.jpg') || secureFacebookImageUrl.toLowerCase().endsWith('.jpeg') ? 'image/jpeg' : 'image/png'} />
        
        {/* Article specific tags */}
        <meta property="article:published_time" content={new Date(meta.date).toISOString()} />
        <meta property="article:author" content={meta.author} />
        <meta property="article:section" content={meta.category} />
        <meta property="article:tag" content={`prop trading, ${meta.category.toLowerCase()}, trading tips`} />
        
        {/* LinkedIn specific tags - Using exact format LinkedIn requested */}
        <meta property="linkedin:owner" content="PropViper" />
        <meta property="linkedin:title" content={meta.title} />
        <meta property="linkedin:description" content={meta.excerpt} />
        <meta property="linkedin:image" content={secureImageUrl} />
        <meta name="author" content={meta.author} />
        
        {/* Facebook specific */}
        {/* Facebook Open Graph tag for article */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.excerpt} />
        <meta property="og:image" content={secureFacebookImageUrl} />
        <meta property="og:image:width" content={imageWidth.toString()} />
        <meta property="og:image:height" content={imageHeight.toString()} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.excerpt} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:site" content="@thepropviper" />
        <meta name="twitter:creator" content="@thepropviper" />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-yellow-100">
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Link to="/blog" className="inline-flex items-center text-yellow-300 hover:text-yellow-400 font-medium">
                <ArrowLeft className="mr-2 w-4 h-4" /> Back to Blog
              </Link>
            </div>

            <div className="bg-white/5 backdrop-blur-lg rounded-xl shadow-lg overflow-hidden mb-10">
              {meta.image ? (
                <img
                  src={meta.image}
                  alt={meta.title}
                  className="w-full h-64 sm:h-96 object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-64 sm:h-96 bg-yellow-900 flex items-center justify-center text-yellow-100 text-xl">
                  Image not available
                </div>
              )}

              <div className="p-6 sm:p-8">
                <div className="flex flex-wrap items-center gap-3 text-sm text-yellow-300 mb-3">
                  <span className="bg-yellow-800/30 px-3 py-1 rounded-full font-medium">
                    {meta.category}
                  </span>
                  <span>{meta.date}</span>
                  <span className="flex items-center gap-1"><User className="w-4 h-4" />{meta.author}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{meta.readTime}</span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold text-yellow-100 mb-4">
                  {meta.title}
                </h1>
                <p className="text-yellow-400 text-base sm:text-lg mb-4">{meta.excerpt}</p>

                <RotatingCallout title={meta.title} url={canonicalUrl} />
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-lg p-6 sm:p-8 rounded-xl shadow-lg">
              <GlassCTA
                firmId={topFirmId}
                variant="primary"
                position="top"
                promoUsedElsewhere={false}
              />

              <div className="overflow-x-auto">
                <div className="prose prose-lg prose-invert max-w-prose mx-auto">
                  {Content ? <Content /> : <p className="text-red-400">No content found in MDX default export</p>}
                </div>
              </div>

              <div className="mt-20">
                <GlassCTA
                  firmId={bottomFirmId}
                  variant="secondary"
                  position="bottom"
                  promoUsedElsewhere={true}
                />
              </div>

              {relatedPosts.length > 0 && (
                <div className="mt-16">
                  <RelatedPosts posts={relatedPosts.map(convertToBlogPost)} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <BackToTop />
    </>
  );
}
